import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Card, CardContent } from '@/common/ui/card'
import { useCampaignForm, useCampaignActions } from './hooks'
import { ExamplePreview, ImageZoomModal, StagesCard } from './components'
import type { BrokeFlexMessageCampaignModel } from '../../types'

interface BrokeFlexMessageCampaignFormProps {
	mode: 'create' | 'edit'
	campaignId?: string
	initialData?: BrokeFlexMessageCampaignModel
	onSuccess: () => void
}

export default function BrokeFlexMessageCampaignForm({
	mode,
	campaignId,
	initialData,
	onSuccess,
}: BrokeFlexMessageCampaignFormProps) {
	const [activeTab, setActiveTab] = useState('0')
	const [showImageZoom, setShowImageZoom] = useState(false)

	const { form, stageFields, appendStage, removeStage, moveStage } =
		useCampaignForm({ mode, initialData })

	const { handlePreview, handleSubmit, isPending } = useCampaignActions({
		mode,
		campaignId,
		initialData,
		onSuccess,
	})

	const addStage = () => {
		if (stageFields.length >= 4) {
			toast.error('สามารถสร้างได้สูงสุด 4 ระยะเท่านั้น')
			return
		}
		const newIndex = stageFields.length
		appendStage({
			minutesAfterTrigger: stageFields.length === 0 ? 0 : 5,
			message: 'คุณยังมีโอกาสรับโบนัสอีกมากมาย!',
			maxItemsToShow: 3,
			order: stageFields.length,
			items: [],
		})
		setActiveTab(String(newIndex))
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
				<Card>
					<CardContent className="pt-6">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>ชื่อแคมเปญ</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="กรอกชื่อแคมเปญเพื่อง่ายต่อการจัดการ"
										/>
									</FormControl>
									<FormDescription>
										ชื่อแคมเปญที่ใช้สำหรับระบุและจัดการแคมเปญนี้
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</CardContent>
				</Card>

				<ExamplePreview onZoomClick={() => setShowImageZoom(true)} />

				<ImageZoomModal open={showImageZoom} onOpenChange={setShowImageZoom} />

				<StagesCard
					form={form}
					stageFields={stageFields}
					activeTab={activeTab}
					onActiveTabChange={setActiveTab}
					onAddStage={addStage}
					onRemoveStage={removeStage}
					onMoveStage={moveStage}
					onPreview={handlePreview}
					mode={mode}
					campaignId={campaignId}
					initialData={initialData}
				/>

				<div className="flex justify-end gap-3">
					<Button type="submit" disabled={isPending}>
						{isPending
							? mode === 'edit'
								? 'กำลังบันทึก...'
								: 'กำลังสร้าง...'
							: mode === 'edit'
								? 'บันทึกการแก้ไข'
								: 'สร้างแคมเปญ'}
					</Button>
				</div>
			</form>
		</Form>
	)
}
